/**
 * Collection Carousel
 * Native horizontal scrolling first; JavaScript only enhances interaction.
 */

if (!customElements.get('collection-carousel')) {
  class CollectionCarousel extends HTMLElement {
    connectedCallback() {
      this._connectFrame = requestAnimationFrame(() => {
        this._connectFrame = null;
        this.init();
      });
    }

    disconnectedCallback() {
      if (this._connectFrame !== null) {
        cancelAnimationFrame(this._connectFrame);
        this._connectFrame = null;
      }
      this.cleanup();
    }

    init() {
      if (this.isInitialized) return;

      this.track = this.querySelector('[data-carousel-track]');
      this.slides = [...this.querySelectorAll('[data-carousel-slide]')];
      this.prevBtn = this.querySelector('[data-carousel-prev]');
      this.nextBtn = this.querySelector('[data-carousel-next]');
      this.progressTrack = this.querySelector('[data-carousel-progress]');
      this.progressBar = this.querySelector('[data-carousel-progress-bar]');
      this.indicatorsContainer = this.querySelector('[data-carousel-indicators]');

      if (!this.track || this.slides.length === 0) return;

      this.isInitialized = true;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;

      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplaySpeed = this.clamp(Number.parseFloat(this.dataset.autoplaySpeed) || 20, 5, 50);
      this.dragEnabled = this.dataset.dragEnabled !== 'false';
      this.snapEnabled = this.dataset.snapEnabled !== 'false';

      if (this.snapEnabled) this.track.dataset.snapEnabled = 'true';
      if (this.dragEnabled) this.track.dataset.dragEnabled = 'true';

      this.isDragging = false;
      this.hasDragged = false;
      this.suppressClickUntil = 0;
      this.dragStartX = 0;
      this.dragScrollLeft = 0;

      this.isTouching = false;
      this.isInteracting = false;
      this.isHovering = false;
      this.hasFocusWithin = false;
      this.isAutoplayPaused = false;

      this.autoplayDirection = 1;
      this.animationFrameId = null;
      this.resumeTimeout = null;
      this.interactionEndTimeout = null;
      this.scrollEndTimeout = null;
      this.resizeTimeout = null;
      this.lastTimestamp = null;
      this.exactScrollLeft = this.track.scrollLeft;
      this.maxScroll = 0;
      this.scrollTicking = false;

      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.bindHandlers();
      this.setupListeners();
      this.setupResizeObserver();
      this.recalculate();
      this.buildIndicators();

      if (this.autoplayEnabled && !this.reducedMotionQuery.matches) {
        this.startAutoplay();
      }
    }

    bindHandlers() {
      this._animate = this._animate.bind(this);
      this._onScroll = this._onScroll.bind(this);
      this._onScrollEnd = this._onScrollEnd.bind(this);
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);
      this._onClickCapture = this._onClickCapture.bind(this);
      this._onDragStart = this._onDragStart.bind(this);
      this._onTouchStart = this._onTouchStart.bind(this);
      this._onTouchEnd = this._onTouchEnd.bind(this);
      this._onWheel = this._onWheel.bind(this);
      this._onPrevClick = this._onPrevClick.bind(this);
      this._onNextClick = this._onNextClick.bind(this);
      this._onMouseEnter = this._onMouseEnter.bind(this);
      this._onMouseLeave = this._onMouseLeave.bind(this);
      this._onFocusIn = this._onFocusIn.bind(this);
      this._onFocusOut = this._onFocusOut.bind(this);
      this._onVisibilityChange = this._onVisibilityChange.bind(this);
      this._onReducedMotionChange = this._onReducedMotionChange.bind(this);
    }

    setupListeners() {
      const passive = { passive: true, signal: this.signal };

      this.track.addEventListener('scroll', this._onScroll, passive);
      this.track.addEventListener('wheel', this._onWheel, passive);
      this.track.addEventListener('touchstart', this._onTouchStart, passive);
      this.track.addEventListener('touchend', this._onTouchEnd, passive);
      this.track.addEventListener('touchcancel', this._onTouchEnd, passive);
      this.track.addEventListener('dragstart', this._onDragStart, { signal: this.signal });

      if (this.dragEnabled) {
        this.track.addEventListener('pointerdown', this._onPointerDown, { signal: this.signal });
        this.track.addEventListener('click', this._onClickCapture, { capture: true, signal: this.signal });
      }

      if (this.prevBtn) this.prevBtn.addEventListener('click', this._onPrevClick, { signal: this.signal });
      if (this.nextBtn) this.nextBtn.addEventListener('click', this._onNextClick, { signal: this.signal });

      this.addEventListener('mouseenter', this._onMouseEnter, { signal: this.signal });
      this.addEventListener('mouseleave', this._onMouseLeave, { signal: this.signal });
      this.addEventListener('focusin', this._onFocusIn, { signal: this.signal });
      this.addEventListener('focusout', this._onFocusOut, { signal: this.signal });

      document.addEventListener('visibilitychange', this._onVisibilityChange, { signal: this.signal });
      this.reducedMotionQuery.addEventListener('change', this._onReducedMotionChange, { signal: this.signal });
    }

    cleanup() {
      if (!this.isInitialized) return;

      this.stopAutoplay({ preserveSnapState: false });
      this.clearTimer('interactionEndTimeout');
      this.clearTimer('scrollEndTimeout');
      this.clearTimer('resizeTimeout');

      document.removeEventListener('pointermove', this._onPointerMove);
      document.removeEventListener('pointerup', this._onPointerUp);
      document.removeEventListener('pointercancel', this._onPointerUp);

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }

      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }

      this.isInitialized = false;
    }

    clearTimer(name) {
      if (this[name] !== null) {
        clearTimeout(this[name]);
        this[name] = null;
      }
    }

    clamp(value, min, max) {
      return Math.min(Math.max(value, min), max);
    }

    recalculate() {
      if (!this.track) return;

      this.maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const hasOverflow = this.slides.length > 1 && this.maxScroll > 1;
      this.dataset.hasOverflow = hasOverflow ? 'true' : 'false';

      if (this.track.scrollLeft > this.maxScroll) {
        this.track.scrollLeft = this.maxScroll;
      }

      this.exactScrollLeft = this.track.scrollLeft;
      this.updateArrows();
      this.updateProgress();
      this.updateActiveIndicator();

      if (!hasOverflow) {
        this.stopAutoplay({ preserveSnapState: false });
      }
    }

    getScrollStep() {
      if (!this.slides.length) return 0;
      const gap = Number.parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 0;
      return this.slides[0].getBoundingClientRect().width + gap;
    }

    updateArrows() {
      const current = Math.round(this.track.scrollLeft);
      const max = Math.round(this.maxScroll);

      if (this.prevBtn) {
        const disabled = current <= 2;
        this.prevBtn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        this.prevBtn.disabled = disabled;
      }

      if (this.nextBtn) {
        const disabled = current >= max - 2;
        this.nextBtn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        this.nextBtn.disabled = disabled;
      }
    }

    updateProgress() {
      if (!this.progressTrack || !this.progressBar) return;

      const progress = this.maxScroll > 0
        ? this.clamp(this.track.scrollLeft / this.maxScroll, 0, 1)
        : 0;

      this.progressBar.style.transform = `scaleX(${progress})`;
      this.progressTrack.setAttribute('aria-valuenow', String(Math.round(progress * 100)));
    }

    _onScroll() {
      if (!this.scrollTicking) {
        this.scrollTicking = true;
        requestAnimationFrame(() => {
          this.updateArrows();
          this.updateProgress();
          this.updateActiveIndicator();
          this.scrollTicking = false;
        });
      }

      this.clearTimer('scrollEndTimeout');
      this.scrollEndTimeout = setTimeout(this._onScrollEnd, 180);
    }

    _onScrollEnd() {
      this.scrollEndTimeout = null;
      this.exactScrollLeft = this.track.scrollLeft;

      if (!this.isTouching && !this.isDragging) {
        this.isInteracting = false;
      }

      this.resumeAutoplay(500);
    }

    _onPrevClick() {
      if (this.prevBtn?.disabled) return;
      this.beginManualInteraction();
      this.track.scrollBy({ left: -this.getScrollStep(), behavior: 'smooth' });
      this.scheduleInteractionEnd(450);
    }

    _onNextClick() {
      if (this.nextBtn?.disabled) return;
      this.beginManualInteraction();
      this.track.scrollBy({ left: this.getScrollStep(), behavior: 'smooth' });
      this.scheduleInteractionEnd(450);
    }

    _onWheel() {
      this.beginManualInteraction();
      this.scheduleInteractionEnd(280);
    }

    _onTouchStart() {
      this.isTouching = true;
      this.beginManualInteraction();
    }

    _onTouchEnd() {
      this.isTouching = false;
      this.isInteracting = false;
      this.clearTimer('scrollEndTimeout');
      this.scrollEndTimeout = setTimeout(this._onScrollEnd, 220);
    }

    _onMouseEnter() {
      this.isHovering = true;
      this.pauseAutoplay({ preserveSnapState: true });
    }

    _onMouseLeave() {
      this.isHovering = false;
      this.resumeAutoplay(250);
    }

    _onFocusIn() {
      this.hasFocusWithin = true;
      this.pauseAutoplay({ preserveSnapState: true });
    }

    _onFocusOut(event) {
      if (!this.contains(event.relatedTarget)) {
        this.hasFocusWithin = false;
        this.resumeAutoplay(250);
      }
    }

    _onVisibilityChange() {
      if (document.hidden) {
        this.pauseAutoplay({ preserveSnapState: true });
      } else {
        this.resumeAutoplay(300);
      }
    }

    _onReducedMotionChange(event) {
      if (event.matches) {
        this.stopAutoplay({ preserveSnapState: false });
      } else {
        this.resumeAutoplay(200);
      }
    }

    beginManualInteraction() {
      this.isInteracting = true;
      this.pauseAutoplay({ preserveSnapState: false });
    }

    endManualInteraction() {
      this.isInteracting = false;
      this.resumeAutoplay(650);
    }

    scheduleInteractionEnd(delay) {
      this.clearTimer('interactionEndTimeout');
      this.interactionEndTimeout = setTimeout(() => {
        this.interactionEndTimeout = null;
        if (!this.isDragging && !this.isTouching) this.endManualInteraction();
      }, delay);
    }

    _onPointerDown(event) {
      if (event.pointerType === 'touch' || event.button !== 0) return;

      this.beginManualInteraction();
      this.isDragging = true;
      this.hasDragged = false;
      this.dragStartX = event.clientX;
      this.dragScrollLeft = this.track.scrollLeft;
      this.track.dataset.dragging = 'true';

      document.addEventListener('pointermove', this._onPointerMove);
      document.addEventListener('pointerup', this._onPointerUp);
      document.addEventListener('pointercancel', this._onPointerUp);
    }

    _onPointerMove(event) {
      if (!this.isDragging) return;

      const deltaX = event.clientX - this.dragStartX;

      if (!this.hasDragged) {
        if (Math.abs(deltaX) <= 6) return;
        this.hasDragged = true;
      }

      event.preventDefault();
      this.track.scrollLeft = this.dragScrollLeft - deltaX;
      this.exactScrollLeft = this.track.scrollLeft;
      this.updateProgress();
    }

    _onPointerUp() {
      if (!this.isDragging) return;

      const didDrag = this.hasDragged;
      this.isDragging = false;
      this.hasDragged = false;
      delete this.track.dataset.dragging;

      document.removeEventListener('pointermove', this._onPointerMove);
      document.removeEventListener('pointerup', this._onPointerUp);
      document.removeEventListener('pointercancel', this._onPointerUp);

      if (didDrag) this.suppressClickUntil = performance.now() + 350;
      this.endManualInteraction();
    }

    _onClickCapture(event) {
      if (performance.now() < this.suppressClickUntil) {
        event.preventDefault();
        event.stopPropagation();
      }
    }

    _onDragStart(event) {
      if (event.target.closest('.collection-carousel-card')) {
        event.preventDefault();
      }
    }

    startAutoplay() {
      if (
        !this.autoplayEnabled ||
        this.reducedMotionQuery.matches ||
        this.dataset.hasOverflow !== 'true' ||
        this.isInteracting ||
        this.isTouching ||
        this.isDragging ||
        this.isHovering ||
        this.hasFocusWithin ||
        document.hidden
      ) {
        return;
      }

      this.stopAutoplay({ preserveSnapState: true });
      this.isAutoplayPaused = false;
      this.lastTimestamp = null;
      this.exactScrollLeft = this.track.scrollLeft;

      if (this.exactScrollLeft >= this.maxScroll - 1) this.autoplayDirection = -1;
      if (this.exactScrollLeft <= 1) this.autoplayDirection = 1;

      this.track.dataset.animating = 'true';
      this.animationFrameId = requestAnimationFrame(this._animate);
    }

    pauseAutoplay({ preserveSnapState = true } = {}) {
      this.isAutoplayPaused = true;

      if (this.animationFrameId !== null) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }

      if (!preserveSnapState) delete this.track.dataset.animating;
    }

    stopAutoplay({ preserveSnapState = false } = {}) {
      if (this.animationFrameId !== null) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }

      this.clearTimer('resumeTimeout');
      this.lastTimestamp = null;

      if (!preserveSnapState && this.track) delete this.track.dataset.animating;
    }

    resumeAutoplay(delay = 300) {
      this.clearTimer('resumeTimeout');

      if (!this.autoplayEnabled || this.reducedMotionQuery.matches) return;

      this.resumeTimeout = setTimeout(() => {
        this.resumeTimeout = null;
        this.isAutoplayPaused = false;

        if (
          !this.isInteracting &&
          !this.isTouching &&
          !this.isDragging &&
          !this.isHovering &&
          !this.hasFocusWithin &&
          !document.hidden
        ) {
          this.startAutoplay();
        }
      }, delay);
    }

    _animate(timestamp) {
      if (
        this.isAutoplayPaused ||
        !this.autoplayEnabled ||
        this.isInteracting ||
        this.isTouching ||
        this.isDragging ||
        this.isHovering ||
        this.hasFocusWithin ||
        document.hidden
      ) {
        this.animationFrameId = null;
        return;
      }

      if (this.maxScroll <= 1) {
        this.animationFrameId = null;
        delete this.track.dataset.animating;
        return;
      }

      if (this.lastTimestamp === null) {
        this.lastTimestamp = timestamp;
        this.animationFrameId = requestAnimationFrame(this._animate);
        return;
      }

      const deltaSeconds = Math.min((timestamp - this.lastTimestamp) / 1000, 0.05);
      this.lastTimestamp = timestamp;
      this.exactScrollLeft += this.autoplaySpeed * deltaSeconds * this.autoplayDirection;

      if (this.exactScrollLeft >= this.maxScroll) {
        this.exactScrollLeft = this.maxScroll;
        this.autoplayDirection = -1;
      } else if (this.exactScrollLeft <= 0) {
        this.exactScrollLeft = 0;
        this.autoplayDirection = 1;
      }

      this.track.scrollLeft = this.exactScrollLeft;
      this.updateProgress();
      this.updateArrows();
      this.animationFrameId = requestAnimationFrame(this._animate);
    }

    setupResizeObserver() {
      this.resizeObserver = new ResizeObserver(() => {
        this.clearTimer('resizeTimeout');
        this.resizeTimeout = setTimeout(() => {
          this.resizeTimeout = null;
          this.recalculate();
          this.buildIndicators();
          this.resumeAutoplay(200);
        }, 100);
      });

      this.resizeObserver.observe(this.track);
      this.slides.forEach((slide) => this.resizeObserver.observe(slide));
    }

    getVisibleCount() {
      if (!this.slides.length) return 1;
      const step = this.getScrollStep();
      return step > 0 ? Math.max(1, Math.floor((this.track.clientWidth + 1) / step)) : 1;
    }

    buildIndicators() {
      if (!this.indicatorsContainer) return;

      const visible = this.getVisibleCount();
      const totalPages = Math.max(1, Math.ceil(this.slides.length / visible));
      this.indicatorsContainer.replaceChildren();

      for (let index = 0; index < totalPages; index += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'collection-carousel__dot';
        dot.setAttribute('aria-label', `Page ${index + 1} sur ${totalPages}`);
        dot.addEventListener('click', () => {
          this.beginManualInteraction();
          this.track.scrollTo({
            left: index * visible * this.getScrollStep(),
            behavior: 'smooth',
          });
          this.scheduleInteractionEnd(450);
        });
        this.indicatorsContainer.appendChild(dot);
      }

      this.updateActiveIndicator();
    }

    updateActiveIndicator() {
      if (!this.indicatorsContainer) return;
      const dots = [...this.indicatorsContainer.querySelectorAll('.collection-carousel__dot')];
      if (!dots.length) return;

      const visible = this.getVisibleCount();
      const pageWidth = visible * this.getScrollStep();
      const current = this.clamp(pageWidth > 0 ? Math.round(this.track.scrollLeft / pageWidth) : 0, 0, dots.length - 1);

      dots.forEach((dot, index) => {
        dot.classList.toggle('active', index === current);
        dot.setAttribute('aria-current', index === current ? 'true' : 'false');
      });
    }

    revealBlock(blockId) {
      if (!blockId) return;
      const slide = this.querySelector(`[data-carousel-block-id="${CSS.escape(blockId)}"]`);
      if (!slide) return;

      this.pauseAutoplay({ preserveSnapState: true });
      const target = slide.offsetLeft - (this.track.clientWidth - slide.offsetWidth) / 2;
      this.track.scrollTo({ left: this.clamp(target, 0, this.maxScroll), behavior: 'smooth' });
    }
  }

  customElements.define('collection-carousel', CollectionCarousel);

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      const carousel = event.target.querySelector('collection-carousel');
      if (carousel && !carousel.isInitialized) carousel.init();
    });

    document.addEventListener('shopify:section:unload', (event) => {
      const carousel = event.target.querySelector('collection-carousel');
      if (carousel) carousel.cleanup();
    });

    document.addEventListener('shopify:block:select', (event) => {
      const carousel = event.target.closest?.('collection-carousel') || event.target.querySelector?.('collection-carousel');
      if (!carousel) return;
      carousel.revealBlock(event.detail?.blockId);
    });

    document.addEventListener('shopify:block:deselect', (event) => {
      const carousel = event.target.closest?.('collection-carousel') || event.target.querySelector?.('collection-carousel');
      if (carousel) carousel.resumeAutoplay(400);
    });
  }
}
